<ng-container *transloco="let t">
  @if (isBottomSheet()) {
    <div
      id="borrower-identity-bs-backdrop"
      class="fixed inset-0 z-[70] flex items-end bg-black/50 keyboard-aware-container"
      [style.padding-bottom.px]="keyboardHeight()"
      role="dialog"
      aria-modal="true"
      aria-labelledby="borrower-identity-bs-title"
      tabindex="-1"
      (click)="onCancel()"
      (keydown.enter)="onCancel()"
      (keydown.escape)="onCancel()"
    >
      <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
      <div
        id="borrower-identity-bs-container"
        class="bg-white w-full rounded-t-2xl shadow-xl flex flex-col overflow-hidden borrower-identity-bottom-sheet-slide-up"
        [style.max-height]="'calc(100dvh - env(safe-area-inset-top, 0px) - ' + keyboardHeight() + 'px)'"
        style="padding-bottom: env(safe-area-inset-bottom, 0px)"
        (click)="$event.stopPropagation()"
      >
        <div
          id="borrower-identity-bs-header"
          class="flex items-center justify-between gap-4 px-4 py-3 bg-black rounded-t-2xl shrink-0"
        >
          <span id="borrower-identity-bs-title" class="font-dm text-base font-semibold text-white">
            {{ modalTitle() }}
          </span>
          <button
            id="borrower-identity-bs-close-btn"
            type="button"
            class="flex items-center justify-center size-8 shrink-0 bg-transparent border-none cursor-pointer text-white"
            [attr.aria-label]="t('commonAriaClose')"
            [disabled]="isLoading()"
            (click)="onCancel()"
          >
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <div id="borrower-identity-bs-content" class="p-4 overflow-y-auto flex-1">
          <ng-container *ngTemplateOutlet="borrowerContent; context: { $implicit: t }" />
        </div>

        <div id="borrower-identity-bs-footer" class="px-4 py-4 border-t border-ld-border shrink-0 flex flex-col gap-3">
          @if (!isProfileSelectionStep()) {
            <button
              id="borrower-identity-bs-enter-btn"
              type="button"
              class="btn-primary-ld-purple order-1"
              [disabled]="isEnterDisabled()"
              (click)="onEnter()"
            >
              @if (isLoading()) {
                <mat-spinner diameter="16" class="w-4! h-4! [&_circle]:stroke-white!"></mat-spinner>
              }
              {{ t('modalsVerifyBorrowerVerifyButton') }}
            </button>
          }
          <button
            id="borrower-identity-bs-cancel-btn"
            type="button"
            class="btn-outline-ld-purple order-2"
            [disabled]="isLoading()"
            (click)="onCancel()"
          >
            {{ t('commonButtonsCancel') }}
          </button>
        </div>
      </div>
    </div>
  } @else {
    <ld-base-modal
      [title]="modalTitle()"
      [size]="'medium'"
      [isLoading]="isLoading()"
      [ariaLabelledBy]="'borrower-identity-modal-title'"
      (closeModal)="onCancel()"
    >
      <div modal-content>
        <ng-container *ngTemplateOutlet="borrowerContent; context: { $implicit: t }" />
      </div>

      <div modal-footer>
        <button
          id="borrower-identity-cancel-btn"
          type="button"
          class="btn-outline-ld-purple"
          [disabled]="isLoading()"
          (click)="onCancel()"
        >
          {{ t('commonButtonsCancel') }}
        </button>
        @if (!isProfileSelectionStep()) {
          <button
            id="borrower-identity-enter-btn"
            type="button"
            class="btn-primary-ld-purple"
            [disabled]="isEnterDisabled()"
            (click)="onEnter()"
          >
            @if (isLoading()) {
              <mat-spinner diameter="16" class="w-4! h-4! [&_circle]:stroke-white!"></mat-spinner>
            }
            {{ t('modalsVerifyBorrowerVerifyButton') }}
          </button>
        }
      </div>
    </ld-base-modal>
  }

  <ng-template #borrowerContent let-t>
    @if (isProfileSelectionStep()) {
      <p id="borrower-profile-selection-description" class="font-dm text-sm text-ld-neutral-600 mb-6">
        {{ t('profileMenuSwitchProfile') }}
      </p>

      <div id="borrower-profile-selection-list" class="flex flex-col gap-3" role="list">
        @for (borrower of effectiveBorrowers(); track borrower.id) {
          <button
            [id]="'borrower-profile-option-' + borrower.id"
            #borrowerProfileOption
            type="button"
            class="flex items-center justify-between gap-4 w-full p-4 text-left border rounded-xl cursor-pointer focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2"
            [class.border-ld-primary]="isSelected(borrower)"
            [class.border-ld-border]="!isSelected(borrower)"
            [attr.aria-pressed]="isSelected(borrower)"
            (click)="onSelectBorrower(borrower)"
          >
            <span [id]="'borrower-profile-name-' + borrower.id" class="font-dm text-base font-medium text-ld-neutral">
              {{ getBorrowerDisplayName(borrower) }}
            </span>
            @if (isSelected(borrower)) {
              <mat-icon
                [id]="'borrower-profile-selected-icon-' + borrower.id"
                class="text-ld-primary"
                aria-hidden="true"
              >
                check
              </mat-icon>
            }
          </button>
        }
      </div>
    } @else {
      @if (effectiveProfileSelectionMode() && isSwitchMode()) {
        <button
          id="borrower-change-profile-btn"
          type="button"
          class="mb-4 min-h-[44px] min-w-[44px] cursor-pointer text-left text-sm font-dm font-medium text-ld-primary underline focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2"
          (click)="onChangeProfile()"
        >
          {{ t('profileMenuSwitchProfile') }}
        </button>
      }

      <p id="borrower-identity-description" class="font-dm text-sm text-ld-neutral-600 mb-6">
        @if (isSwitchMode() && displayBorrower(); as borrower) {
          {{ t('modalsBorrowerIdentitySwitchDescription', { name: getBorrowerDisplayName(borrower) }) }}
        } @else {
          {{ t('verifyIdentityModalDescription') }}
        }
      </p>

      <div id="borrower-identity-options" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        @if (isSwitchMode()) {
          @if (displayBorrower(); as borrower) {
            <div [id]="'borrower-card-' + borrower.id" class="p-5 text-left border border-ld-primary rounded-xl">
              <div [id]="'borrower-icon-' + borrower.id" class="mb-4">
                <mat-icon class="text-ld-neutral-500 text-3xl w-8 h-8">person_search</mat-icon>
              </div>

              <p [id]="'borrower-type-' + borrower.id" class="font-dm text-sm font-medium text-ld-neutral-600 mb-0">
                {{ t(getBorrowerTypeLabel(borrower)) }}
              </p>

              <p [id]="'borrower-name-' + borrower.id" class="font-dm text-lg font-semibold text-ld-neutral mb-2">
                {{ getBorrowerDisplayName(borrower) }}
              </p>

              @if (borrower.verifyRequired) {
                <div [id]="'borrower-ssn-' + borrower.id" class="mt-4">
                  <label
                    [for]="'ssn-input-' + borrower.id"
                    class="block text-xs font-dm font-normal text-ld-neutral-500 mb-1"
                  >
                    {{ t('ssnLast4') }}
                  </label>
                  <input
                    #ssnInput
                    [id]="'ssn-input-' + borrower.id"
                    [name]="'ssn-' + borrower.id"
                    type="text"
                    inputmode="numeric"
                    enterkeyhint="done"
                    maxlength="4"
                    pattern="[0-9]{4}"
                    autocomplete="off"
                    glance_masked="true"
                    [value]="getSsnValue(borrower.id)"
                    (input)="onSsnInput($event, borrower.id)"
                    (keydown)="onKeydown($event)"
                    class="w-full px-3 py-3 text-base border border-ld-border rounded-lg font-dm focus:outline-none focus:ring-2 focus:ring-ld-primary focus:border-transparent [-webkit-text-security:disc]"
                    [placeholder]="t('modalsBorrowerIdentitySsnPlaceholder')"
                  />
                </div>
              }
            </div>
          }
        } @else {
          @for (borrower of effectiveBorrowers(); track borrower.id) {
            <div
              [id]="'borrower-card-' + borrower.id"
              role="button"
              tabindex="0"
              class="p-5 text-left border rounded-xl transition-all cursor-pointer"
              [class.border-ld-primary]="isSelected(borrower)"
              [class.border-ld-border]="!isSelected(borrower)"
              [class.hover:border-ld-primary-200]="!isSelected(borrower)"
              (click)="onSelectBorrower(borrower)"
              (keydown.enter)="onSelectBorrower(borrower)"
              (keydown.space)="$event.preventDefault(); onSelectBorrower(borrower)"
            >
              <div [id]="'borrower-icon-' + borrower.id" class="mb-4">
                <mat-icon class="text-ld-neutral-500 text-3xl w-8 h-8">person_search</mat-icon>
              </div>

              <p [id]="'borrower-type-' + borrower.id" class="font-dm text-sm font-medium text-ld-neutral-600 mb-0">
                {{ t(getBorrowerTypeLabel(borrower)) }}
              </p>

              <p [id]="'borrower-name-' + borrower.id" class="font-dm text-lg font-semibold text-ld-neutral mb-2">
                {{ getBorrowerDisplayName(borrower) }}
              </p>

              @if (borrower.verifyRequired) {
                <div [id]="'borrower-ssn-' + borrower.id" class="mt-4">
                  <label
                    [for]="'ssn-input-' + borrower.id"
                    class="block text-xs font-dm font-normal text-ld-neutral-500 mb-1"
                  >
                    {{ t('ssnLast4') }}
                  </label>
                  @if (isSelected(borrower)) {
                    <input
                      #ssnInput
                      [id]="'ssn-input-' + borrower.id"
                      [name]="'ssn-' + borrower.id"
                      type="text"
                      inputmode="numeric"
                      enterkeyhint="done"
                      maxlength="4"
                      pattern="[0-9]{4}"
                      autocomplete="off"
                      glance_masked="true"
                      [value]="getSsnValue(borrower.id)"
                      (input)="onSsnInput($event, borrower.id)"
                      (keydown)="onKeydown($event)"
                      (click)="$event.stopPropagation()"
                      class="w-full px-3 py-3 text-base border border-ld-border rounded-lg font-dm focus:outline-none focus:ring-2 focus:ring-ld-primary focus:border-transparent [-webkit-text-security:disc]"
                      [placeholder]="t('modalsBorrowerIdentitySsnPlaceholder')"
                    />
                  } @else {
                    <div
                      [id]="'ssn-placeholder-' + borrower.id"
                      class="w-full px-3 py-3 text-base border border-ld-border rounded-lg font-dm text-ld-neutral-400"
                    >
                      {{ t('modalsBorrowerIdentitySsnPlaceholder') }}
                    </div>
                  }
                </div>
              }
            </div>
          }
        }
      </div>
    }

    @if (errorMessage()) {
      <div id="borrower-identity-error" class="p-3 bg-red-50 border border-red-200 rounded-lg mt-4" role="alert">
        <p id="borrower-identity-error-text" class="text-sm font-dm text-red-700">{{ t(errorMessage()!) }}</p>
      </div>
    }
  </ng-template>
</ng-container>
